import React, { useMemo, useState } from 'react';
import { AlertTriangle, BadgeEuro, Loader2, RotateCcw, Upload } from 'lucide-react';
import { Button } from '../Button';
import { ServiceItem, ServiceTransaction } from '../../types';
import {
    manualBillingEntries,
    manualBillingInvoiceNumber,
    manuallyBillableQuantity,
} from '../../services/manualBillingLogic';

interface Props {
    service: ServiceItem;
    serviceLog: ServiceTransaction[];
    /**
     * Beteiligte des Aufteilungsschluessels.
     *
     * `null` heisst UNBEKANNT (laedt noch oder Abfrage fehlgeschlagen) und
     * sperrt den Vorgang — genau wie beim Einlesen einer Alt-Rechnung. Eine
     * Menge ohne Zuordnung sperrte spaeter jede Teilrechnung, an einer Stelle,
     * die niemand mehr mit dieser Buchung in Verbindung bringt.
     * `[]` heisst ungeteilt.
     */
    splitParticipants: { partyId: string; customerId: string; name: string }[] | null;
    /** Wirft nie — der Text der Rueckmeldung landet im Dialog. */
    onBook: (payload: {
        quantity: number;
        billedOn: string;
        invoiceNumber: string;
        reason: string;
        customerId: string;
        splitPartyId: string;
    }) => Promise<{ ok: boolean; message: string }>;
    onUndo: (entryId: string, reason: string) => Promise<{ ok: boolean; message: string }>;
    onClose: () => void;
}

/** Heutiger Kalendertag in Ortszeit — die Obergrenze des Abrechnungsdatums. */
const heute = (): string => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const tag = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('de-DE') : 'ohne Datum');

/**
 * MANUELLE VERBUCHUNG — eine in der Vergangenheit abgerechnete Menge stempeln,
 * obwohl der Beleg dazu fehlt.
 *
 * Der Dialog nennt den besseren Weg zuerst: liegt die alte Rechnung als PDF
 * oder Foto vor, gehoert sie eingelesen ("Letzte Rechnung einlesen"). Dann
 * traegt die Buchung eine Belegnummer, und der Betrag wird zusaetzlich als
 * Vorrechnung abgezogen. Dieser Vorgang hier ist der Ausweg fuer den Fall, dass
 * es das Dokument nicht mehr gibt — und er sagt genau das im Klartext, weil
 * eine Verbuchung ohne Beleg spaeter niemand mehr erklaeren kann.
 *
 * Abgegrenzt wird gegen die beiden Nachbarvorgaenge derselben Zeile: Verzicht
 * (nie abgerechnet, wird nicht mehr gefordert) und Ruecknahme (nie erbracht).
 */
export const ManualBillingDialog: React.FC<Props> = ({
    service,
    serviceLog,
    splitParticipants,
    onBook,
    onUndo,
    onClose,
}) => {
    const offen = manuallyBillableQuantity(service);
    const einheit = service.unit || '';
    const geteilt = (splitParticipants?.length || 0) > 1;
    const schluesselUnbekannt = splitParticipants === null;

    const buchungen = useMemo(
        () => manualBillingEntries(serviceLog, service.id).filter((t) => (t.quantity || 0) > 0),
        [serviceLog, service.id],
    );
    const zurueckgenommen = useMemo(() => {
        const ids = new Set<string>();
        for (const t of manualBillingEntries(serviceLog, service.id)) {
            if ((t.quantity || 0) < 0) {
                const treffer = /Buchung\s+([^\s)]+)/.exec(t.description || '');
                if (treffer) ids.add(treffer[1]);
            }
        }
        return ids;
    }, [serviceLog, service.id]);

    const [menge, setMenge] = useState<string>(offen > 0 ? String(offen) : '');
    const [datum, setDatum] = useState<string>('');
    const [belegnummer, setBelegnummer] = useState('');
    const [grund, setGrund] = useState('');
    // Gewaehlt wird der TEILNEHMER (partyId), nicht der Auftraggeber: derselbe
    // Auftraggeber kann mehrere Abschnitte haben, und die Buchung gehoert an
    // den Abschnitt (projectSplit.billedByParty).
    const [teilnehmer, setTeilnehmer] = useState<string>(
        splitParticipants?.length === 1 ? splitParticipants[0].partyId : '',
    );
    const [laeuft, setLaeuft] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);

    const mengeZahl = Number((menge || '').replace(',', '.'));
    const mengeOk = Number.isFinite(mengeZahl) && mengeZahl > 0 && mengeZahl <= offen;
    const datumOk = /^\d{4}-\d{2}-\d{2}$/.test(datum) && datum <= heute();
    // Entweder die Nummer oder der Grund — irgendetwas muss die Buchung tragen.
    const herkunftOk = belegnummer.trim().length > 0 || grund.trim().length >= 3;
    const kundeOk = !geteilt || Boolean(teilnehmer);
    const bereit = mengeOk && datumOk && herkunftOk && kundeOk && !schluesselUnbekannt;

    const ausfuehren = async (aktion: () => Promise<{ ok: boolean; message: string }>) => {
        setLaeuft(true);
        setFehler(null);
        const ergebnis = await aktion();
        if (ergebnis.ok) {
            onClose();
            return;
        }
        setFehler(ergebnis.message);
        setLaeuft(false);
    };

    return (
        <div className="fixed inset-0 z-[160] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-xl shadow-2xl animate-scale-in max-h-[90vh] overflow-y-auto">
                <div className="flex items-start gap-4 mb-4">
                    <div className="bg-emerald-50 p-3 rounded-full"><BadgeEuro className="w-6 h-6 text-emerald-600" /></div>
                    <div className="min-w-0">
                        <h3 className="font-bold text-lg text-slate-800">Als abgerechnet buchen</h3>
                        <p className="text-sm text-slate-600 mt-1 truncate" title={service.title}>
                            {service.lvPosition ? `${service.lvPosition} — ` : ''}{service.title}
                        </p>
                    </div>
                </div>

                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 mb-4 flex gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-900 leading-relaxed">
                        <strong>Buchung ohne Beleg.</strong> Die Menge gilt danach als abgerechnet und
                        wird nie wieder angeboten — war sie in Wahrheit nicht auf einer Rechnung, fehlt
                        das Geld dauerhaft. Liegt die alte Rechnung als PDF oder Foto vor, gehört sie
                        stattdessen über <strong>„Letzte Rechnung einlesen"</strong>
                        <Upload className="w-3 h-3 inline mx-1 -mt-0.5" />
                        eingelesen: dann hängt an der Buchung ein Beleg, und der Betrag wird
                        zusätzlich als Vorrechnung abgezogen.
                    </p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 mb-4 text-xs text-slate-600 leading-relaxed">
                    Gebucht wird nur der <strong>Leistungsstand</strong>, kein Betrag. Rechnet dieses
                    Projekt <strong>kumulativ</strong> ab, muss die alte Forderung zusätzlich als
                    Vorrechnung hinterlegt sein („Abschlag nachtragen"), sonst zieht der nächste Beleg
                    sie nicht ab und fordert sie erneut.
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="text-[11px] uppercase text-slate-400 font-bold">Erbracht</div>
                        <div className="font-mono font-bold text-slate-700">{service.quantityUsed || 0} {einheit}</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="text-[11px] uppercase text-slate-400 font-bold">Abgerechnet</div>
                        <div className="font-mono font-bold text-emerald-600">{service.quantityBilled || 0} {einheit}</div>
                    </div>
                    <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                        <div className="text-[11px] uppercase text-indigo-400 font-bold">Offen</div>
                        <div className="font-mono font-bold text-indigo-600">{offen} {einheit}</div>
                    </div>
                </div>

                {buchungen.length > 0 && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 mb-4">
                        <div className="text-[11px] uppercase text-slate-400 font-bold mb-2">
                            Bereits von Hand gebucht
                        </div>
                        <div className="space-y-1.5">
                            {buchungen.map((t) => {
                                const weg = zurueckgenommen.has(t.id);
                                const nummer = manualBillingInvoiceNumber(t);
                                return (
                                    <div key={t.id} className="flex items-start justify-between gap-2 text-xs">
                                        <span className={weg ? 'text-slate-400 line-through' : 'text-slate-600'}>
                                            <strong className="font-mono">{t.quantity} {einheit}</strong>
                                            {' '}— abgerechnet am {tag(t.date)}
                                            {nummer ? `, Beleg ${nummer}` : ', ohne Belegnummer'}
                                            <span className="block text-[11px] text-slate-400">
                                                {t.bookedBy || 'unbekannt'}
                                                {t.bookedAt ? `, gebucht am ${tag(t.bookedAt)}` : ''}
                                            </span>
                                        </span>
                                        {!weg && (
                                            <button
                                                type="button"
                                                disabled={laeuft}
                                                onClick={() => ausfuehren(() => onUndo(t.id, grund.trim()))}
                                                className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-800 underline underline-offset-2 disabled:opacity-50"
                                            >
                                                <RotateCcw className="w-3 h-3" /> zurücknehmen
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {offen > 0 ? (
                    <>
                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div>
                                <label className="block text-xs font-bold uppercase text-slate-500 mb-1">
                                    Menge
                                </label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        min={0}
                                        max={offen}
                                        step="any"
                                        value={menge}
                                        disabled={laeuft}
                                        onChange={(e) => setMenge(e.target.value)}
                                        className="w-28 border border-slate-300 rounded-lg px-3 py-2 font-mono focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                    />
                                    <span className="text-sm text-slate-500">von {offen} {einheit}</span>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase text-slate-500 mb-1">
                                    Abgerechnet am (Pflicht)
                                </label>
                                <input
                                    type="date"
                                    value={datum}
                                    max={heute()}
                                    disabled={laeuft}
                                    onChange={(e) => setDatum(e.target.value)}
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                />
                                <p className="text-[11px] text-slate-400 mt-1">
                                    Datum der alten Rechnung. Es datiert die Buchung im Protokoll.
                                </p>
                            </div>
                        </div>

                        <label className="block text-xs font-bold uppercase text-slate-500 mb-1">
                            Belegnummer (optional)
                        </label>
                        <input
                            type="text"
                            value={belegnummer}
                            disabled={laeuft}
                            onChange={(e) => setBelegnummer(e.target.value)}
                            placeholder="z. B. RE-2024-0118 — falls die Nummer bekannt ist"
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-4 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />

                        {geteilt && (
                            <>
                                <label className="block text-xs font-bold uppercase text-slate-500 mb-1">
                                    Auftraggeber / Abschnitt (Pflicht)
                                </label>
                                <select
                                    value={teilnehmer}
                                    disabled={laeuft}
                                    onChange={(e) => setTeilnehmer(e.target.value)}
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-1 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                >
                                    <option value="">— bitte wählen —</option>
                                    {(splitParticipants || []).map((p) => (
                                        <option key={p.partyId} value={p.partyId}>{p.name}</option>
                                    ))}
                                </select>
                                <p className="text-[11px] text-slate-400 mb-4">
                                    Mehrere Auftraggeber teilen sich dieses Projekt. Ohne Zuordnung
                                    sperrt die Menge später jede Teilrechnung.
                                </p>
                            </>
                        )}

                        <label className="block text-xs font-bold uppercase text-slate-500 mb-1">
                            Begründung {belegnummer.trim() ? '(optional)' : '(Pflicht ohne Belegnummer)'}
                        </label>
                        <textarea
                            value={grund}
                            disabled={laeuft}
                            rows={3}
                            onChange={(e) => setGrund(e.target.value)}
                            placeholder="z. B. Abgerechnet im Altsystem, Beleg nicht mehr auffindbar. Abgleich mit der Buchhaltung am 12.03."
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                        <p className="text-[11px] text-slate-400 mt-1 mb-4">
                            Sie steht später im Leistungsprotokoll und in den Projektnotizen — bei
                            einer Buchung ohne Beleg ist sie der einzige Nachweis.
                        </p>

                        {schluesselUnbekannt && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 mb-4 text-xs text-amber-900">
                                Der Aufteilungsschlüssel dieses Projekts steht noch nicht fest. Solange
                                das so ist, wird nicht gebucht — eine Menge ohne Zuordnung sperrte
                                später jede Teilrechnung.
                            </div>
                        )}
                    </>
                ) : (
                    <p className="text-sm text-slate-500 mb-4">
                        An dieser Position ist nichts offen, was noch als abgerechnet gebucht werden
                        könnte.
                    </p>
                )}

                {fehler && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 mb-4 text-sm text-red-700">{fehler}</div>
                )}

                <div className="flex justify-end gap-3">
                    <Button variant="secondary" onClick={onClose} disabled={laeuft}>Abbrechen</Button>
                    {offen > 0 && (
                        <Button
                            variant="primary"
                            disabled={laeuft || !bereit}
                            onClick={() =>
                                ausfuehren(() =>
                                    onBook({
                                        quantity: mengeZahl,
                                        billedOn: datum,
                                        invoiceNumber: belegnummer.trim(),
                                        reason: grund.trim(),
                                        // Beide Stempel: der Teilnehmer traegt
                                        // die Zuordnung, der Auftraggeber bleibt
                                        // fuer Auswertungen daneben stehen.
                                        customerId:
                                            (splitParticipants || []).find((p) => p.partyId === teilnehmer)
                                                ?.customerId || '',
                                        splitPartyId: teilnehmer,
                                    }),
                                )
                            }
                            icon={laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <BadgeEuro className="w-4 h-4" />}
                        >
                            Als abgerechnet buchen
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
